import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  MousePointer2, ArrowLeft, Trophy, History, Target, Zap, X
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Stage Configuration
const STAGES = [
  { name: "Warmup", minTargets: 0, maxTargets: 5, spawnRate: 5000, lifespan: 4000 },
  { name: "Easy", minTargets: 6, maxTargets: 10, spawnRate: 3000, lifespan: 3500 },
  { name: "Medium", minTargets: 11, maxTargets: 15, spawnRate: 2000, lifespan: 3000 },
  { name: "Fast", minTargets: 16, maxTargets: 20, spawnRate: 1000, lifespan: 2500 },
  { name: "Rapid", minTargets: 21, maxTargets: 25, spawnRate: 500, lifespan: 2000 },
  { name: "Extreme", minTargets: 26, maxTargets: 30, spawnRate: 200, lifespan: 1500 },
  { name: "Insane", minTargets: 31, maxTargets: Infinity, spawnRate: 100, lifespan: 1200 },
];

// Scoring functions
const INITIAL_TARGET_SIZE = 60;
const INITIAL_RADIUS = INITIAL_TARGET_SIZE / 2; // 30px

const getStageMultiplier = (stage) => 1.0 + (stage * 0.5);

const calculatePrecision = (clickX, clickY, targetX, targetY, currentRadius, initialRadius = INITIAL_RADIUS) => {
  const distance = Math.hypot(clickX - targetX, clickY - targetY);
  
  // 1. Hit Detection: Did they click inside the currently visible shrinking circle?
  if (distance > currentRadius) return 0.0; 
  
  // 2. Precision Scoring: Base the score on the ABSOLUTE physical distance 
  // from the center, using the initial radius.
  // This rewards clutch clicks on tiny targets!
  return Math.max(0.0, 1.0 - (distance / initialRadius));
};

const calculateHitScore = (rtMs, precision, stage) => {
  const rtSafe = Math.max(rtMs, 100.0);
  const multiplier = getStageMultiplier(stage);
  const hitScore = (1000.0 / rtSafe) * precision * multiplier * 10.0;
  return Math.round(hitScore * 100) / 100;
};

const calculateMisclickPenalty = (stage, penaltyWeight = 8) => {
  const multiplier = getStageMultiplier(stage);
  return Math.round(penaltyWeight * multiplier * 100) / 100;
};

const getCurrentStage = (targetsHit) => {
  for (let i = STAGES.length - 1; i >= 0; i--) {
    if (targetsHit >= STAGES[i].minTargets) return i;
  }
  return 0;
};

export default function PixelPrecisionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  const gameAreaRef = useRef(null);
  
  // Game state
  const [gameState, setGameState] = useState('idle'); // idle, playing, ended
  const [score, setScore] = useState(0);
  const [targetsHit, setTargetsHit] = useState(0);
  const [misclicks, setMisclicks] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [target, setTarget] = useState(null);
  const [targetSize, setTargetSize] = useState(60);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [precisions, setPrecisions] = useState([]);
  
  // Leaderboard & history
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);
  
  // Refs for game loop
  const spawnTimeoutRef = useRef(null);
  const shrinkIntervalRef = useRef(null);
  const targetSpawnTimeRef = useRef(null);
  const gameStateRef = useRef(gameState);
  const targetsHitRef = useRef(0);
  const missedTargetsRef = useRef(0);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    targetsHitRef.current = targetsHit;
  }, [targetsHit]);

  useEffect(() => {
    missedTargetsRef.current = missedTargets;
  }, [missedTargets]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (!token || !savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
    fetchLeaderboard();
    fetchAttempts();
  }, [navigate]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/pixel/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/pixel/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const saveResult = useCallback(async (finalScore, hits, clicks, stage, avgRt, avgPrec) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/pixel/results`, {
        score: finalScore,
        targets_hit: hits,
        misclicks: clicks,
        stage_reached: stage + 1,
        avg_reaction_time: avgRt,
        avg_precision: avgPrec
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Result saved!');
      fetchLeaderboard();
      fetchAttempts();
    } catch (e) {
      toast.error('Failed to save result');
    }
  }, []);

  const endGame = useCallback((finalScore, finalHits, finalMisclicks, finalStage, rts, precs) => {
    setGameState('ended');
    clearTimeout(spawnTimeoutRef.current);
    clearInterval(shrinkIntervalRef.current);
    setTarget(null);
    
    // Calculate averages
    const avgRt = rts.length > 0 ? rts.reduce((a, b) => a + b, 0) / rts.length : 0;
    const avgPrec = precs.length > 0 ? precs.reduce((a, b) => a + b, 0) / precs.length : 0;
    
    // Save result
    saveResult(finalScore, finalHits, finalMisclicks, finalStage, avgRt, avgPrec);
  }, [saveResult]);

  const spawnTarget = useCallback(() => {
    if (gameStateRef.current !== 'playing') return;
    
    const gameArea = gameAreaRef.current;
    if (!gameArea) return;
    
    const rect = gameArea.getBoundingClientRect();
    const padding = 50;
    
    const x = padding + Math.random() * (rect.width - padding * 2 - INITIAL_TARGET_SIZE);
    const y = padding + Math.random() * (rect.height - padding * 2 - INITIAL_TARGET_SIZE);
    
    const newTarget = {
      id: Date.now(),
      x,
      y,
      size: INITIAL_TARGET_SIZE,
      spawnTime: Date.now()
    };
    
    setTarget(newTarget);
    setTargetSize(INITIAL_TARGET_SIZE);
    targetSpawnTimeRef.current = Date.now();
    
    // Get current stage config
    const currentHits = targetsHitRef.current;
    const stageConfig = STAGES[getCurrentStage(currentHits)];
    
    // Shrink the target over its lifespan
    const shrinkInterval = 50;
    const shrinkAmount = INITIAL_TARGET_SIZE / (stageConfig.lifespan / shrinkInterval);
    
    if (shrinkIntervalRef.current) clearInterval(shrinkIntervalRef.current);
    
    shrinkIntervalRef.current = setInterval(() => {
      setTargetSize(prev => {
        const newSize = prev - shrinkAmount;
        if (newSize <= 0) {
          clearInterval(shrinkIntervalRef.current);
          
          const currentMissed = missedTargetsRef.current;
          const newMissed = currentMissed + 1;
          setMissedTargets(newMissed);
          missedTargetsRef.current = newMissed;
          
          if (newMissed >= 3) {
            // Game over - will be handled by the component
            setGameState('ended');
            setTarget(null);
          } else {
            // Continue game
            setTarget(null);
            toast.error(`Missed! ${3 - newMissed} lives left`, { duration: 1000 });
            // Schedule next target
            const stage = getCurrentStage(targetsHitRef.current);
            const stageConf = STAGES[stage];
            let spawnRate = stageConf.spawnRate;
            if (stage === 6 && targetsHitRef.current >= 31) {
              const extraTargets = targetsHitRef.current - 31;
              spawnRate = Math.max(50, 100 - extraTargets * 5);
            }
            spawnTimeoutRef.current = setTimeout(() => {
              spawnTarget();
            }, spawnRate);
          }
          return 0;
        }
        return newSize;
      });
    }, shrinkInterval);
  }, []);

  const scheduleNextTarget = useCallback(() => {
    if (gameStateRef.current !== 'playing') return;
    
    const stage = getCurrentStage(targetsHitRef.current);
    const stageConfig = STAGES[stage];
    
    let spawnRate = stageConfig.spawnRate;
    if (stage === 6 && targetsHitRef.current >= 31) {
      const extraTargets = targetsHitRef.current - 31;
      spawnRate = Math.max(50, 100 - extraTargets * 5);
    }
    
    spawnTimeoutRef.current = setTimeout(() => {
      spawnTarget();
    }, spawnRate);
  }, [spawnTarget]);

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setTargetsHit(0);
    setMisclicks(0);
    setMissedTargets(0);
    setCurrentStage(0);
    setReactionTimes([]);
    setPrecisions([]);
    setTarget(null);
    targetsHitRef.current = 0;
    missedTargetsRef.current = 0;
    
    setTimeout(() => {
      spawnTarget();
    }, 500);
  }, [spawnTarget]);

  const handleTargetClick = useCallback((e) => {
    if (!target || gameStateRef.current !== 'playing') return;
    
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const centerX = targetSize / 2;
    const centerY = targetSize / 2;
    
    const reactionTime = Date.now() - targetSpawnTimeRef.current;
    const precision = calculatePrecision(clickX, clickY, centerX, centerY, targetSize / 2);
    const stage = getCurrentStage(targetsHitRef.current);
    const hitScore = calculateHitScore(reactionTime, precision, stage);
    
    setScore(prev => prev + hitScore);
    setTargetsHit(prev => {
      const newHits = prev + 1;
      targetsHitRef.current = newHits;
      return newHits;
    });
    setReactionTimes(prev => [...prev, reactionTime]);
    setPrecisions(prev => [...prev, precision]);
    
    const newStage = getCurrentStage(targetsHitRef.current + 1);
    setCurrentStage(newStage);
    
    clearInterval(shrinkIntervalRef.current);
    setTarget(null);
    scheduleNextTarget();
  }, [target, targetSize, scheduleNextTarget]);

  const handleMisclick = useCallback((e) => {
    if (gameStateRef.current !== 'playing') return;
    
    const stage = getCurrentStage(targetsHitRef.current);
    const penalty = calculateMisclickPenalty(stage);
    
    setScore(prev => Math.max(0, prev - penalty));
    setMisclicks(prev => prev + 1);
    
    toast.error(`Misclick! -${penalty} pts`, { duration: 1000 });
  }, []);

  const resetGame = useCallback(() => {
    setGameState('idle');
    setScore(0);
    setTargetsHit(0);
    setMisclicks(0);
    setMissedTargets(0);
    setCurrentStage(0);
    setTarget(null);
    targetsHitRef.current = 0;
    missedTargetsRef.current = 0;
    clearTimeout(spawnTimeoutRef.current);
    clearInterval(shrinkIntervalRef.current);
  }, []);

  // Save result when game ends
  useEffect(() => {
    if (gameState === 'ended' && user) {
      const avgRt = reactionTimes.length > 0 ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length : 0;
      const avgPrec = precisions.length > 0 ? precisions.reduce((a, b) => a + b, 0) / precisions.length : 0;
      saveResult(score, targetsHit, misclicks, currentStage, avgRt, avgPrec);
    }
  }, [gameState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(spawnTimeoutRef.current);
      clearInterval(shrinkIntervalRef.current);
    };
  }, []);

  const stageConfig = STAGES[currentStage];
  const bestAttempt = attempts.length > 0 
    ? attempts.reduce((best, curr) => curr.score > best.score ? curr : best)
    : null;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#020617]/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Tests</span>
          </Button>
          
          <div className="flex items-center gap-2 font-heading font-bold text-lg">
            <MousePointer2 className="w-5 h-5 text-emerald-500" />
            <span>Pixel Perfect Precision</span>
          </div>

          <div className="text-sm text-slate-400">
            {user?.username}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-3xl blur opacity-20"></div>
                <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-6">
                  
                  {/* Game Stats Bar */}
                  <div className="flex justify-between items-center mb-4 px-2">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-xs text-slate-500 uppercase font-heading">Score</div>
                        <div className="text-2xl font-mono-display font-bold text-emerald-400">{Math.round(score)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 uppercase font-heading">Hits</div>
                        <div className="text-2xl font-mono-display font-bold text-white">{targetsHit}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 uppercase font-heading">Misclicks</div>
                        <div className="text-2xl font-mono-display font-bold text-red-400">{misclicks}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 uppercase font-heading">Lives</div>
                        <div className="text-2xl font-mono-display font-bold text-orange-400">{Math.max(0, 3 - missedTargets)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 uppercase font-heading">Stage</div>
                      <div className="text-lg font-heading font-bold text-cyan-400">
                        {currentStage + 1}: {stageConfig.name}
                      </div>
                    </div>
                  </div>

                  {/* Game Canvas */}
                  <div 
                    ref={gameAreaRef}
                    className={`relative w-full h-[400px] sm:h-[500px] rounded-2xl overflow-hidden transition-colors ${
                      gameState === 'playing' ? 'cursor-crosshair psychedelic-bg' : ''
                    }`}
                    style={{
                      background: gameState !== 'playing' ? '#0f172a' : undefined
                    }}
                    onClick={gameState === 'playing' ? handleMisclick : undefined}
                    data-testid="game-area"
                  >
                    {/* Idle State */}
                    {gameState === 'idle' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                          <Target className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="font-heading text-2xl font-bold mb-3">Pixel Perfect Precision</h2>
                        <p className="text-slate-400 text-center max-w-md mb-6 px-4">
                          Click targets as fast as possible before they disappear. 
                          You have 3 lives. Misclicks cost points.
                        </p>
                        <Button 
                          onClick={startGame}
                          className="px-8 py-6 bg-emerald-600 hover:bg-emerald-500 text-xl font-heading font-bold uppercase"
                          data-testid="start-btn"
                        >
                          Start Game
                        </Button>
                      </div>
                    )}

                    {/* Playing State - Target */}
                    {gameState === 'playing' && target && (
                      <div
                        className="absolute transition-all duration-50 cursor-pointer"
                        style={{
                          left: target.x,
                          top: target.y,
                          width: targetSize,
                          height: targetSize,
                        }}
                        onClick={handleTargetClick}
                        data-testid="target"
                      >
                        <div 
                          className="w-full h-full rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/50 flex items-center justify-center"
                        >
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                      </div>
                    )}

                    {/* Game Over State */}
                    {gameState === 'ended' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/90">
                        <X className="w-16 h-16 text-red-500 mb-4" />
                        <h2 className="font-heading text-3xl font-bold mb-2">Game Over!</h2>
                        <p className="text-slate-400 mb-6">You ran out of lives!</p>
                        
                        <div className="grid grid-cols-3 gap-6 mb-8">
                          <div className="text-center">
                            <div className="text-3xl font-mono-display font-bold text-emerald-400">{Math.round(score)}</div>
                            <div className="text-xs text-slate-500 uppercase">Final Score</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-mono-display font-bold text-white">{targetsHit}</div>
                            <div className="text-xs text-slate-500 uppercase">Targets Hit</div>
                          </div>
                          <div className="text-center">
                            <div className="text-3xl font-mono-display font-bold text-cyan-400">Stage {currentStage + 1}</div>
                            <div className="text-xs text-slate-500 uppercase">{stageConfig.name}</div>
                          </div>
                        </div>
                        
                        <div className="flex gap-4">
                          <Button 
                            onClick={startGame}
                            className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 font-heading font-bold uppercase"
                            data-testid="retry-btn"
                          >
                            Try Again
                          </Button>
                          <Button 
                            onClick={resetGame}
                            variant="outline"
                            className="px-8 py-4 border-slate-700 hover:bg-slate-800 font-heading"
                          >
                            Exit
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Personal Stats */}
              {bestAttempt && (
                <div className="mt-6 grid grid-cols-4 gap-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Best Score</div>
                    <div className="text-xl font-mono-display font-bold text-emerald-400">
                      {Math.round(bestAttempt.score)}
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Best Stage</div>
                    <div className="text-xl font-mono-display font-bold text-cyan-400">
                      {Math.max(...attempts.map(a => a.stage_reached))}
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Attempts</div>
                    <div className="text-xl font-mono-display font-bold text-white">
                      {attempts.length}
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Avg Score</div>
                    <div className="text-xl font-mono-display font-bold text-slate-300">
                      {Math.round(attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Stage Info */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Stage Info</h3>
                </div>
                <div className="space-y-3 text-sm">
                  {STAGES.map((stage, idx) => (
                    <div 
                      key={idx}
                      className={`flex justify-between items-center p-2 rounded-lg ${
                        idx === currentStage && gameState === 'playing' 
                          ? 'bg-emerald-500/20 border border-emerald-500/30' 
                          : idx < currentStage 
                            ? 'bg-slate-800/50 text-slate-500' 
                            : 'bg-slate-800/30'
                      }`}
                    >
                      <span className="font-heading">{idx + 1}. {stage.name}</span>
                      <span className="text-slate-400 text-xs">{stage.minTargets}-{stage.maxTargets === Infinity ? '∞' : stage.maxTargets}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Leaderboard</h3>
                </div>
                <ScrollArea className="h-48">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-20">{entry.username}</span>
                          </div>
                          <span className="font-mono-display font-bold text-emerald-400">
                            {Math.round(entry.score)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-48">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 10).map((attempt, idx) => (
                        <div 
                          key={attempt.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                        >
                          <div className="text-sm">
                            <span className="text-slate-400">Stage {attempt.stage_reached}</span>
                            <span className="text-slate-600 mx-2">•</span>
                            <span className="text-slate-500">{attempt.targets_hit} hits</span>
                          </div>
                          <span className="font-mono-display font-bold text-emerald-400">
                            {Math.round(attempt.score)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start playing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
